import { parseAssetProcessingActivityReviewRowForFE, parseAssetRopaWarningFlagsForFE } from 'config/api/ropaReviews/ropaReviews.parsers';
import type {
  AssetProcessingActivityReviewRow,
  AssetRopaReviewStatus,
  AssetRopaWarningFlags,
} from 'config/api/ropaReviews/ropaReviews.types';

import type {
  AssetRopaReviewOverlayDTO,
  AssetRopaReviewOverlayEmptyStateDTO,
  OwnerAssetRopaListItemDTO,
} from './assetRopaPermanentWorkspace.types';
import type { AssetRopaReviewOverlay, OwnerWorkspaceAsset } from './assetRopaWorkspace.types';

export const EMPTY_ASSET_ROPA_WARNINGS: AssetRopaWarningFlags = {
  dpiaIndicated: false,
  dpiaRequiredUnapproved: false,
  thirdCountryProcessing: false,
  sensitivePersonalData: false,
  confirmedNoProcessingActivities: false,
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
};

const readNumericId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const readAssetId = (record: Record<string, unknown>): number | null => {
  const direct = readNumericId(record.asset_id);
  if (direct !== null) {
    return direct;
  }

  const nestedAsset = asRecord(record.asset);
  return nestedAsset ? readNumericId(nestedAsset.id) : null;
};

const isPermanentWorkspaceListItem = (item: Record<string, unknown>): boolean =>
  'completeness_status' in item || 'gdpr_relevance_status' in item || 'review_overlay' in item;

/**
 * Detail retrieve may wrap the row (`{ asset: row }`) or nest the Asset model
 * on a permanent row (`{ id, completeness_status, asset: { id } }`).
 * Prefer the permanent AssetRopa row and only unwrap wrappers.
 */
export const resolveMineDetailRecord = (data: unknown): Record<string, unknown> | null => {
  const payload = asRecord(data);
  if (!payload) {
    return null;
  }

  if (isPermanentWorkspaceListItem(payload)) {
    return payload;
  }

  return asRecord(payload.asset) ?? asRecord(payload.result) ?? payload;
};

const isLegacyActiveReviewListItem = (item: Record<string, unknown>): boolean =>
  ('review_status' in item || 'warnings' in item || 'asset_ropa_id' in item) && !isPermanentWorkspaceListItem(item);

/**
 * SAF-864 / SAF-878: inventory is always permanent workspace.
 * Envelope `mode` and `active_review` must not switch list parsing (`mode: "active_review"` no longer exists).
 */
export const resolveOwnerAssetRopaResponseMode = (payload?: Record<string, unknown>): 'permanent_workspace' =>
  payload ? 'permanent_workspace' : 'permanent_workspace';

const parsePermanentWorkspaceWarnings = (dto: OwnerAssetRopaListItemDTO): AssetRopaWarningFlags => {
  if (dto.review_overlay?.warnings) {
    return parseAssetRopaWarningFlagsForFE(dto.review_overlay.warnings);
  }

  return {
    ...EMPTY_ASSET_ROPA_WARNINGS,
    sensitivePersonalData: Boolean(dto.handles_sensitive_personal_data),
  };
};

const parseOwnerOverlayRowForFE = (
  dto: AssetRopaReviewOverlayDTO['processing_activity_reviews'][number],
): AssetProcessingActivityReviewRow => {
  const base = parseAssetProcessingActivityReviewRowForFE(dto);

  return {
    ...base,
    canAccept: false,
    canRequestClarification: false,
    canOpen: dto.can_open !== false,
    canEdit: dto.can_edit !== false,
  };
};

const parseReviewOverlayEmptyState = (
  dto: AssetRopaReviewOverlayEmptyStateDTO | null | undefined,
): AssetRopaReviewOverlay['emptyState'] => {
  if (!dto) {
    return null;
  }

  return {
    isEmpty: Boolean(dto.is_empty),
    messageKey: dto.message_key,
  };
};

export const parseAssetRopaReviewOverlayForFE = (dto: AssetRopaReviewOverlayDTO | null | undefined): AssetRopaReviewOverlay | null => {
  if (!dto) {
    return null;
  }

  return {
    assetRopaReviewId: dto.asset_ropa_review_id,
    reviewStatus: dto.review_status as AssetRopaReviewStatus,
    permanentRopaStatus: dto.permanent_ropa_status,
    warnings: dto.warnings ? parseAssetRopaWarningFlagsForFE(dto.warnings) : EMPTY_ASSET_ROPA_WARNINGS,
    hasProcessingActivities: dto.has_processing_activities,
    emptyState: parseReviewOverlayEmptyState(dto.empty_state),
    processingActivityReviews: (dto.processing_activity_reviews ?? []).map(parseOwnerOverlayRowForFE),
  };
};

export const parsePermanentWorkspaceListItemForFE = (dto: OwnerAssetRopaListItemDTO): OwnerWorkspaceAsset => {
  const assetRopaId = dto.id;
  const reviewOverlay = parseAssetRopaReviewOverlayForFE(dto.review_overlay);
  const overlayRows = reviewOverlay?.processingActivityReviews ?? [];
  const detailRows = (dto as OwnerAssetRopaListItemDTO & { processing_activity_reviews?: unknown[] }).processing_activity_reviews ?? [];
  let processingActivityReviews = overlayRows;

  if (processingActivityReviews.length === 0 && Array.isArray(detailRows)) {
    processingActivityReviews = detailRows.map(row =>
      parseOwnerOverlayRowForFE(row as AssetRopaReviewOverlayDTO['processing_activity_reviews'][number]),
    );
  }

  return {
    id: dto.id,
    assetRopaId,
    assetId: dto.asset_id,
    assetName: dto.asset_name,
    assetTypeId: dto.asset_type_id,
    assetTypeName: dto.asset_type_name,
    ownerName: dto.owner_name ?? null,
    formalOwnerId: dto.formal_owner_id ?? null,
    formalOwnerName: dto.formal_owner_name ?? null,
    operationalOwnerMembershipId: dto.operational_owner_membership_id,
    operationalOwnerName: dto.operational_owner_name,
    processingActivitiesCount: dto.processing_activities_count,
    permanentRopaStatus: reviewOverlay?.permanentRopaStatus ?? 'not_requested',
    reviewStatus: reviewOverlay?.reviewStatus ?? ('accepted' as AssetRopaReviewStatus),
    warnings: reviewOverlay?.warnings ?? parsePermanentWorkspaceWarnings(dto),
    processingActivityReviews,
    menuActions: {},
    gdprRelevanceStatus: dto.gdpr_relevance_status,
    informationalCompleteness: dto.completeness_status,
    lastUpdatedAt: dto.updated_at,
    lastUpdatedByName: dto.last_updated_by_name ?? null,
    canEdit: dto.can_edit,
    includedInCurrentReview: dto.included_in_current_review === true,
    reviewOverlay,
  };
};

const parseDetailCountrySummary = (raw: unknown): string | undefined => {
  const summary = asRecord(raw);
  if (!summary) {
    return undefined;
  }

  const codes = Array.isArray(summary.country_codes)
    ? summary.country_codes.filter((code): code is string => typeof code === 'string' && code.length > 0)
    : [];

  return codes.length > 0 ? codes.join(', ') : undefined;
};

const parsePermanentWorkspaceDetailActivityForFE = (raw: unknown): AssetProcessingActivityReviewRow | null => {
  const dto = asRecord(raw);
  if (!dto) {
    return null;
  }

  const assetProcessingActivityId = readNumericId(dto.asset_processing_activity_id) ?? readNumericId(dto.id);
  if (assetProcessingActivityId === null) {
    return null;
  }

  const dpiaDisplayStatus =
    (typeof dto.dpia_display_status === 'string' && dto.dpia_display_status) ||
    (typeof dto.dpia_indication_status === 'string' && dto.dpia_indication_status) ||
    '';

  return {
    id: assetProcessingActivityId,
    assetProcessingActivityId,
    processingActivityId: readNumericId(dto.processing_activity_id) ?? 0,
    processingActivityName: typeof dto.processing_activity_name === 'string' ? dto.processing_activity_name : '',
    reviewStatus: 'accepted',
    activePurposesCount: readNumericId(dto.active_purposes_count) ?? 0,
    dpiaDisplayStatus,
    dpiaId: readNumericId(dto.dpia_id),
    dpiaCount: readNumericId(dto.dpia_count) ?? 0,
    hasPreviousDpias: dto.has_previous_dpias === true,
    updatedAt: typeof dto.updated_at === 'string' ? dto.updated_at : '',
    canAccept: false,
    canRequestClarification: false,
    canOpen: dto.can_open !== false,
    canEdit: dto.can_edit !== false,
    completenessStatus: dto.completeness_status === 'complete' ? 'complete' : 'incomplete',
    countriesSummary: parseDetailCountrySummary(dto.country_summary),
  };
};

const parsePermanentWorkspaceDetailActivities = (record: Record<string, unknown>): AssetProcessingActivityReviewRow[] => {
  if (Array.isArray(record.processing_activities)) {
    return record.processing_activities
      .map(parsePermanentWorkspaceDetailActivityForFE)
      .filter((row): row is AssetProcessingActivityReviewRow => row !== null);
  }

  if (Array.isArray(record.processing_activity_reviews)) {
    return record.processing_activity_reviews.map(row =>
      parseOwnerOverlayRowForFE(row as AssetRopaReviewOverlayDTO['processing_activity_reviews'][number]),
    );
  }

  return [];
};

export const isOwnerAssetRopaDetailPayload = (raw: unknown): boolean => {
  const record = asRecord(raw);
  if (!record) {
    return false;
  }

  return readNumericId(record.asset_ropa_id) !== null && Array.isArray(record.processing_activities);
};

const parseOwnerAssetRopaDetailRecordForFE = (record: Record<string, unknown>): OwnerWorkspaceAsset | null => {
  const assetRopaId = readNumericId(record.asset_ropa_id) ?? readNumericId(record.id);
  const assetId = readAssetId(record);
  if (assetRopaId === null || assetId === null) {
    return null;
  }

  const processingActivityReviews = parsePermanentWorkspaceDetailActivities(record);
  const gdprRelevanceStatus = record.gdpr_relevance_status === 'no_longer_indicated' ? 'no_longer_indicated' : 'currently_indicated';
  const informationalCompleteness =
    record.completeness_status === 'complete' || record.completeness_status === 'no_processing_activities'
      ? record.completeness_status
      : 'incomplete';

  return {
    id: assetRopaId,
    assetRopaId,
    assetId,
    assetName: typeof record.asset_name === 'string' ? record.asset_name : '',
    assetTypeId: readNumericId(record.asset_type_id) ?? 0,
    assetTypeName: typeof record.asset_type_name === 'string' ? record.asset_type_name : '',
    ownerName: typeof record.owner_name === 'string' ? record.owner_name : null,
    formalOwnerId: readNumericId(record.formal_owner_id),
    formalOwnerName: typeof record.formal_owner_name === 'string' ? record.formal_owner_name : null,
    operationalOwnerMembershipId: readNumericId(record.operational_owner_membership_id),
    operationalOwnerName: typeof record.operational_owner_name === 'string' ? record.operational_owner_name : null,
    processingActivitiesCount: processingActivityReviews.length,
    permanentRopaStatus: 'not_requested',
    reviewStatus: 'accepted',
    warnings: EMPTY_ASSET_ROPA_WARNINGS,
    processingActivityReviews,
    menuActions: {},
    gdprRelevanceStatus,
    informationalCompleteness,
    lastUpdatedAt: typeof record.updated_at === 'string' ? record.updated_at : '',
    lastUpdatedByName: null,
    canEdit: record.can_edit !== false,
    includedInCurrentReview: record.included_in_current_review === true,
    reviewOverlay: parseAssetRopaReviewOverlayForFE((record.review_overlay as AssetRopaReviewOverlayDTO | null | undefined) ?? null),
  };
};

export const isPermanentWorkspaceAssetPayload = (raw: unknown): boolean => {
  const record = asRecord(raw);
  if (!record) {
    return false;
  }

  return isPermanentWorkspaceListItem(record) && !isLegacyActiveReviewListItem(record);
};

export const parsePermanentWorkspaceAssetDetailForFE = (data: unknown): OwnerWorkspaceAsset => {
  const nestedAsset = resolveMineDetailRecord(data) ?? {};
  const parsed = parseOwnerAssetRopaDetailRecordForFE(nestedAsset);

  if (!parsed) {
    throw new Error('Invalid permanent asset-ropa/mine detail response payload.');
  }

  return parsed;
};

/** @deprecated Prefer parsePermanentWorkspaceListItemForFE for list rows. */
export const parsePermanentWorkspaceAssetForFE = parsePermanentWorkspaceListItemForFE;
